import React, { Component, Fragment } from 'react';
import MenuItem from '@mui/material/MenuItem';
import withStyles from '@mui/styles/withStyles';
import InputAdornment from '@mui/material/InputAdornment';
import PlacesAutocomplete, { geocodeByPlaceId } from '../../lib/PlacesAutoComplete';
import Paper from '../layout/Paper';
import { joinDefined } from '../../utils/strings';
import TextField from './TextField';
import { applyAll, getProperty, isNullOrUndefined } from '../../utils/objects';
import { getValidationDecorations } from '../../utils/validation';
import Button from './Button';
import CustomAddressModal from '../modal/CustomAddressModal';
import Spinner from '../loading/Spinner';
import MapMarkerIcon from '../icon/MapMarkerIcon';
import { CloseIcon } from '../IconIndex';
import { Popper } from '@mui/material';
import Typography from '@mui/material/Typography';

class AddressAutocomplete extends Component {
    state = {
        inputValue: null,
        focused: null,
        objectId: null,
        firstLoad: true,
        showCustomAddressModal: false
    };

    static getDerivedStateFromProps(parentProps, oldState) {
        if (isNullOrUndefined(parentProps.componentFields)) return null;

        const getValueFunc =
            (parentProps.form && (parentProps.form.getState || parentProps.form.getField)) || (_ => null);

        if (!(parentProps.form && parentProps.form.loading) && !!oldState.firstLoad) {
            //first load
            return { inputValue: getValueFromProps(parentProps), objectId: getValueFunc('ID'), firstLoad: false };
        } else if (parentProps.form && parentProps.form.loading && oldState.objectId !== null) {
            //reset for next
            return { inputValue: null, objectId: null };
        } else if (!oldState.focused && getValueFromProps(parentProps) !== oldState.inputValue) {
            // form values changed unexpectedly, so update state
            return { inputValue: getValueFromProps(parentProps) };
        }

        return null;
    }

    render() {
        const { inputValue, showCustomAddressModal } = this.state;
        const { form, onlySuburbs, componentFields, name, label } = this.props;

        const searchOptions = {};

        if (window.google && window.google.maps && window.google.maps.LatLng) {
            const googleLatLng = new window.google.maps.LatLng(-34, 151);
            searchOptions.locationBias = {
                center: {
                    lat: googleLatLng.lat(),
                    lng: googleLatLng.lng()
                },
                radius: 2000
            };
        }

        if (!(window.google && window.google.maps && window.google.maps.places)) {
            return (
                <div style={{ width: '100%' }}>
                    {Object.values(componentFields).map((fName, idx) => (
                        <TextField key={idx + fName} form={form} name={fName} label={fName.replace(/.*?\./gi, '')} />
                    ))}
                </div>
            );
        }

        if (!!onlySuburbs) searchOptions.includedPrimaryTypes = ['(cities)'];

        return (
            <Fragment>
                <PlacesAutocomplete
                    value={inputValue || ''}
                    onChange={this.onChange}
                    onSelect={this.onSelect}
                    onError={this.handleError}
                    searchOptions={searchOptions}
                    highlightFirstSuggestion
                >
                    {this.renderContent}
                </PlacesAutocomplete>
                <CustomAddressModal
                    onClose={() => this.setState({ showCustomAddressModal: false })}
                    onApply={() => this.onCustomAddressModalApply()}
                    open={showCustomAddressModal}
                    name={name + '_Custom'}
                    form={form}
                    label={label}
                    customAddressFields={componentFields}
                />
            </Fragment>
        );
    }

    getComponents(geoCodeComponents) {
        const {
            subpremise,
            street_number,
            route,
            locality,
            administrative_area_level_1,
            country,
            postal_code
        } = geoCodeComponents.reduce((map, c) => {
            let property = c.types[0];
            /*
            kind of hacky, but weird. if you enter a capital city, it craps out and loses the city name, because
            it expects 'locality' to be c.types[0]. but its 'colloquial_area' instead. not sure exactly the best
            fix for this, so for the moment, just swap it to locality.
            */
            if (
                c.types.indexOf('locality') !== -1 ||
                c.types.indexOf('sublocality') !== -1 ||
                c.types.indexOf('establishment') !== -1
            )
                property = 'locality';

            map[property] = c;
            return map;
        }, {});

        const num = joinDefined([subpremise && subpremise.short_name, street_number && street_number.short_name], '/');
        return {
            line1: joinDefined([num, route && route.long_name], ' '),
            city: locality && locality.short_name,
            state: administrative_area_level_1 && administrative_area_level_1.short_name,
            country: country && country.long_name,
            code: postal_code && postal_code.short_name
        };
    }

    onBlur = () => {
        setTimeout(() => {
            this.setState({
                focused: false,
                inputValue: getValueFromProps(this.props)
            });
        }, 150);
    };

    onFocus = e => {
        this.setState({ focused: e.currentTarget, inputValue: e.target.value });
    };

    onChange = inputValue => {
        this.setState({ inputValue });
    };

    onClear = () => {
        const that = this;
        const { form, componentFields, onSelect, name } = that.props;
        const newState = Object.keys(componentFields).reduce((acc, componentName) => {
            const formFieldName = componentFields[componentName];
            acc[formFieldName] = null;
            return acc;
        }, {});
        if (typeof form !== 'undefined') {
            if (form.setField) {
                form.setField(newState);
            } else {
                form.setState(newState);
            }
        }
        if (onSelect) onSelect('', newState);
        if (typeof form !== 'undefined' && form.getValidation) {
            form.getValidation(name, true);
        }
        that.setState({
            inputValue: getValueFromProps(that.props)
        });
    };

    onSelect = (inputValue, placeId) => {
        const that = this;
        geocodeByPlaceId(placeId).then(results => {
            const { onReturnValue } = this.props;
            if (!isNullOrUndefined(onReturnValue)) {
                onReturnValue(inputValue);
            }

            const { form, componentFields, onSelect, name } = that.props;

            if (!results.length) return;
            const components = that.getComponents(results[0].address_components);
            const newState = Object.keys(componentFields).reduce((acc, componentName) => {
                const formFieldName = componentFields[componentName];
                acc[formFieldName] = components[componentName] || null;
                return acc;
            }, {});

            if (typeof form !== 'undefined') {
                if (form.setField) {
                    form.setField(newState);
                } else {
                    form.setState(newState);
                }
            }
            if (onSelect) onSelect(inputValue, newState);

            if (form && !form.getValidation) {
                // eslint-disable-next-line no-console
                console.warn(name + ' form does not contain validation function!');
            } else if (form && name) {
                form.getValidation(name, true);
            }

            that.setState({ inputValue: getValueFromProps(that.props) });
        });
    };

    handleError = (status, clearSuggestions) => {
        // eslint-disable-next-line no-console
        console.error('Google Maps API returned error with status: ', status);
        clearSuggestions();
    };

    renderContent = ({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
        const {
            id,
            placeholder = 'Search for street address...',
            label,
            required,
            allowCustomAddress,
            componentFields,
            readOnly,
            disabled,
            classes,
            className = ''
        } = this.props;
        const { inputValue, focused } = this.state;
        const inputProps = getInputProps({ placeholder });
        inputProps.onBlur = applyAll(inputProps.onBlur, this.onBlur);
        inputProps.onFocus = applyAll(inputProps.onFocus, this.onFocus);
        inputProps.label = label;
        inputProps.required = required;

        const decorations = getValidationDecorations(this.props, componentFields, '');

        return (
            <div className="address-autocomplete">
                <div className="input-container">
                    <TextField
                        {...inputProps}
                        id={id}
                        validationResult={decorations.validationResult}
                        readOnly={readOnly}
                        disabled={disabled}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" className={classes.iconContainer}>
                                    {!!loading ? <Spinner size="xs" /> : <MapMarkerIcon className={classes.icon} />}
                                </InputAdornment>
                            ),
                            endAdornment: !readOnly && !disabled && (
                                <InputAdornment position="end">
                                    {!!allowCustomAddress && !inputValue && (
                                        <Button
                                            title="Enter a specific location"
                                            text="Add Specific&hellip;"
                                            variant="link add-new-button"
                                            disabled={disabled}
                                            onClick={() => this.setState({ showCustomAddressModal: true })}
                                        />
                                    )}
                                    {!!inputValue && (
                                        <span title="Remove">
                                            <CloseIcon
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: 'grey',
                                                    padding: 1,
                                                    fontSize: 12,
                                                    borderRadius: 36,
                                                    color: 'white'
                                                }}
                                                onClick={this.onClear}
                                            />
                                        </span>
                                    )}
                                </InputAdornment>
                            )
                        }}
                    />
                    <Popper
                        className={classes.popper}
                        open={!!focused}
                        anchorEl={focused?.parentElement}
                        placement="bottom-start"
                        style={{ width: focused?.parentElement?.clientWidth }}
                    >
                        {this.renderSuggestions(suggestions, getSuggestionItemProps, loading)}
                    </Popper>
                </div>
            </div>
        );
    };

    renderSuggestions(suggestions, getSuggestionItemProps, isLoading) {
        const { focused, inputValue } = this.state;
        if (!focused || !inputValue) return false;
        const { classes, allowCustomAddress } = this.props;
        const wasValue = getValueFromProps(this.props);
        return (
            <Paper className={classes.paper} square>
                {!!suggestions.length ? (
                    suggestions.map((suggestion, idx) => this.renderSuggestion(suggestion, idx, getSuggestionItemProps))
                ) : wasValue !== inputValue ? (
                    !!inputValue && ( // new search attempted
                        <Typography className={classes.item}>
                            {isLoading // TODO: there is a tiny delay before isLoading is correct, not sure how to resolve
                                ? 'Searching...'
                                : 'No results, ' +
                                (!allowCustomAddress ? 'try a different search?' : 'need to add specific?')}
                        </Typography>
                    )
                ) : (
                    // just fake the old value
                    <MenuItem selected>{wasValue}</MenuItem>
                )}
            </Paper>
        );
    }

    renderSuggestion(suggestion, index, getSuggestionItemProps) {
        const props = getSuggestionItemProps(suggestion);
        return (
            <MenuItem {...props} className="item" selected={suggestion.active} key={index}>
                {suggestion.description}
            </MenuItem>
        );
    }

    onCustomAddressModalApply() {
        const inputValue = getValueFromProps(this.props);
        this.setState({ inputValue });

        const { form, componentFields, onSelect } = this.props;
        if (onSelect) {
            const newState = Object.keys(componentFields).reduce((acc, key) => {
                acc[key] = form && form.getField(componentFields[key]);
                return acc;
            }, {});
            onSelect(inputValue, newState);
        }
    }
}

const getValueFromProps = props => {
    const { form, componentFields, value } = props;
    if (!form) return value;

    return getValueFromFormState(componentFields, form.fields || form.state);
};

const getValueFromFormState = (componentFields, state) => {
    // Shorthand to get value of address component field
    const v = name => getProperty(state, componentFields[name]);
    const postcode = v('code');
    return joinDefined(
        [
            joinDefined([v('line1'), v('line2')], ' '),
            joinDefined([v('city'), v('state'), !!postcode ? postcode : ''], ' '),
            v('country')
        ],
        ', '
    );
};

export const DefaultAddressComponentFields = {
    businessName: 'Name',
    line1: 'AddressLine1',
    line2: 'AddressLine2',
    city: 'Suburb',
    state: 'State',
    country: 'Country',
    code: 'Postcode'
};

export const createAddressComponentFields = propertyRoot => {
    return {
        businessName: `${propertyRoot}.Name`,
        line1: `${propertyRoot}.AddressLine1`,
        line2: `${propertyRoot}.AddressLine2`,
        city: `${propertyRoot}.Suburb`,
        state: `${propertyRoot}.State`,
        country: `${propertyRoot}.Country`,
        code: `${propertyRoot}.Postcode`
    };
};

const styles = ({ palette }) => ({
    popper: {
        zIndex: 1300,
        clipPath: 'inset(0px -10px -10px -10px)', // no shadow at top
        paddingTop: 3,
        minWidth: 360,
        maxWidth: 'min(95vw, 800px)',
        '&[data-popper-placement=top-start]': {
            clipPath: 'inset(-10px -10px 0px -10px)', // no shadow at bottom
            paddingBottom: 3
        }
    },
    paper: {
        padding: '8px 0',
        borderRadius: 4
    },
    item: {
        padding: '6px 16px'
    },
    iconContainer: {
        alignSelf: 'center',
        width: 24
    },
    icon: {
        fontSize: 24,
        color: palette.primary.main
    }
});

export default withStyles(styles)(AddressAutocomplete);
